import { useCallback, useRef, useState } from 'react';
import { faBan } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

import type { IFormDialogProps } from '~/components/molecules/FormDialog';
import type {
  IUserContentReviewModerateFormProps,
  IUserContentReviewModerateFormValues,
} from './UserContentReviewModerateForm';
import { FormDialog } from '~/components/molecules/FormDialog';
import { UserContentReviewModerateForm } from './UserContentReviewModerateForm';

export type IUserContentReviewModerateFormDialogProps = Pick<
  IFormDialogProps,
  'opened' | 'onClose'
> &
  IUserContentReviewModerateFormProps;

export const UserContentReviewModerateFormDialog: React.FC<
  IUserContentReviewModerateFormDialogProps
> = (props) => {
  const { opened, onClose, onSubmit, ...other } = props;
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleClose = useCallback((): void => {
    setIsSubmitting(false);
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    onClose?.();
  }, [onClose]);

  const handleSubmit = useCallback(
    (values: IUserContentReviewModerateFormValues): Promise<void> => {
      setIsSubmitting(true);

      return Promise.resolve(onSubmit?.(values))
        .then(handleClose)
        .finally(() => setIsSubmitting(false));
    },
    [onSubmit, handleClose],
  );

  return (
    <>
      <FormDialog
        opened={opened}
        onClose={handleClose}
        headline={t('UserContentReviewModerateFormDialog.title')}
        submitButtonLabel={t('UserContentReviewModerateFormDialog.moderate')}
        size="md"
        submitting={isSubmitting}
        icon={<FontAwesomeIcon icon={faBan} />}
      >
        {({ formId }) => (
          <UserContentReviewModerateForm
            id={formId}
            extended
            hideActionBar
            onSubmit={handleSubmit}
            {...other}
          />
        )}
      </FormDialog>
    </>
  );
};
